// The dashboard card catalogue: one entry per dashboard page, used by
// the sidebar nav and the home card grids so the two surfaces render from the
// same data and cannot drift.
//
// Icons are lucide-react names resolved by the rendering side (this module
// stays pure data — no JSX, no component imports). Which cards a user is
// offered, and which sit in their persona's set, is the server's decision
// (`useNavigation`). `module` names the product a core dashboard belongs to,
// whose countries a workspace switch on that page loads.

import type { DashboardId } from "./personaDefinitions";

export type CardThumb =
  | "kpi"
  | "bar"
  | "deck"
  | "table"
  | "map"
  | "wonlost"
  | "funnel"
  | "pricelist"
  | "compare";

// Everything a thumbnail renders beyond its variant shape. All values are
// illustrative set-dressing (generic labels only, never brand or client
// names) — the renderer owns the geometry, this owns the words and numbers.
export interface ThumbSpec {
  variant: CardThumb;
  // Uppercase caption line above the visual (e.g. "ALL REGIONS · LIVE").
  caption?: string;
  // kpi variant: up to three [value, label] stat chips.
  stats?: ReadonlyArray<readonly [string, string]>;
  // bar variant: one label under each of the four bars.
  barLabels?: readonly string[];
}

// The lucide-react icons a card may name. A closed union rather than a bare
// string: the renderer resolves this through a lookup map with a fallback, so a
// typo used to render the wrong icon silently. tsc now rejects it here, and the
// map is asserted to cover every member (see the renderer).
export type CardIconName =
  | "Globe"
  | "BarChart3"
  | "ClipboardCheck"
  | "MapPin"
  | "Store"
  | "Building2"
  | "ShieldCheck";

export interface DashboardCard {
  id: DashboardId;
  label: string;
  description: string;
  route: string;
  icon: CardIconName;
  format: string; // format chip text
  thumb: ThumbSpec;
  module?: string;
}

export const DASHBOARD_CARDS: readonly DashboardCard[] = [
  {
    id: "market_overview",
    label: "Market Overview",
    description: "Your full footprint across regions, markets and platforms.",
    route: "/market_overview",
    icon: "Globe",
    format: "Dashboard",
    thumb: {
      variant: "kpi",
      caption: "ALL REGIONS · LIVE",
      stats: [
        ["24", "MARKETS"],
        ["128k", "DSFS"],
        ["+4%", "TREND"],
      ],
    },
    module: "market_overview",
  },
  {
    id: "market_deepdive",
    label: "Market Deepdive",
    description: "Refresh-over-refresh: outlets won, lost and retained.",
    route: "/market_deepdive",
    icon: "BarChart3",
    format: "Dashboard",
    thumb: { variant: "wonlost", caption: "RETENTION · ACQUISITION · CHURN" },
    module: "market_deepdive",
  },
  {
    id: "execution_view",
    label: "Execution View",
    description: "Outlet-level scores, availability and menu execution.",
    route: "/execution_view",
    icon: "ClipboardCheck",
    format: "Dashboard",
    thumb: {
      variant: "kpi",
      caption: "ONE PERFORMANCE · OUTLETS",
      stats: [
        ["82", "ONE SCORE"],
        ["91%", "AVAIL."],
        ["64%", "COMBOS"],
      ],
    },
    module: "execution_view",
  },
  {
    id: "physical_location",
    label: "Physical Location",
    description: "Outlet analytics on the map: density, coverage, presence.",
    route: "/physical_location",
    icon: "MapPin",
    format: "Dashboard",
    thumb: { variant: "map" },
    module: "physical_location",
  },
  {
    id: "physical_franchise",
    label: "Physical Franchise",
    description: "Franchise footprints, outlet counts and coverage.",
    route: "/physical_franchise",
    icon: "Store",
    format: "Dashboard",
    thumb: {
      variant: "bar",
      caption: "OUTLETS PER FRANCHISE",
      barLabels: ["A", "B", "C", "D"],
    },
    module: "physical_franchise",
  },
  {
    id: "accounts",
    label: "Accounts",
    description: "Account performance across your chains and markets.",
    route: "/accounts",
    icon: "Building2",
    format: "Dashboard",
    thumb: { variant: "table", caption: "ACCOUNT SCORES" },
  },
  {
    id: "key_accounts",
    label: "Key Accounts",
    description: "Key-account performance across your chains and markets.",
    route: "/key_accounts",
    icon: "ShieldCheck",
    format: "Dashboard",
    thumb: { variant: "table", caption: "TOP ACCOUNTS · GAP" },
  },
  {
    id: "global_key_accounts",
    label: "Global Key Accounts",
    description: "Key-account performance rolled up across all markets.",
    route: "/global_key_accounts",
    icon: "Globe",
    format: "Dashboard",
    thumb: { variant: "table", caption: "ALL MARKETS · ROLLUP" },
  },
];

// Bespoke workflow thumbnails, keyed by workflow id. Workflows without an
// entry fall back to a variant inferred from their output format
// (thumbForOutputType in HomeCardThumbs.tsx). Generic set-dressing only —
// never brand, client or franchise names.
export const WORKFLOW_THUMBS: Readonly<Record<string, ThumbSpec>> = {
  "single-franchise-audit": {
    variant: "deck",
    caption: "FRANCHISE AUDIT",
  },
  "multi-franchise-audit": {
    variant: "table",
    caption: "FRANCHISES · SCORES",
  },
  "fsa-deepdive-v2": {
    variant: "funnel",
    caption: "BRAND PRESENCE FUNNEL",
  },
  "platform-distribution-gaps": {
    variant: "compare",
  },
  "menu-price-intelligence": {
    variant: "pricelist",
    caption: "STANDALONE DRINK PRICING",
  },
  "cuisine-audit-deck": {
    variant: "deck",
    caption: "CUISINE AUDIT",
  },
  "brand-performance-deepdive": {
    variant: "bar",
    caption: "SHARE BY BRAND",
    barLabels: ["Own", "C1", "C2", "C3"],
  },
  "image-compliance": {
    variant: "table",
    caption: "IMAGE COMPLIANCE",
  },
  "kpi-tracker": {
    variant: "table",
    caption: "KPI GRID",
  },
  "sku-name-audit": {
    variant: "table",
    caption: "NAMING ISSUES",
  },
  "pack-type-gaps": {
    variant: "bar",
    caption: "PACK MIX",
    barLabels: ["Can", "Btl", "Mix", "Und"],
  },
  "category-market-share": {
    variant: "bar",
    caption: "SHARE BY CATEGORY",
    barLabels: ["Own", "C1", "C2", "C3"],
  },
  "white-spot-opportunities": {
    variant: "bar",
    caption: "OPPORTUNITY SIZE",
    barLabels: ["A", "B", "C", "D"],
  },
  "map-explorer": { variant: "map" },
  "win-back-lost-customers": {
    variant: "table",
    caption: "LOST OUTLETS",
  },
  "custom-data-export": {
    variant: "table",
    caption: "EXPORT PREVIEW",
  },
  "forge-explorer": {
    variant: "kpi",
    caption: "DSF EXPLORER",
    stats: [
      ["78", "SCORE"],
      ["12.4k", "DSF"],
      ["86%", "AVAIL."],
    ],
  },
  "roi-calculator": {
    variant: "kpi",
    caption: "MODELLED UPLIFT",
    stats: [
      ["+8%", "GMV"],
      ["€1.2m", "VALUE"],
      ["14", "MKTS"],
    ],
  },
  "monthly-kpi-report": {
    variant: "deck",
    caption: "MONTHLY KPI REPORT",
  },
  "country-insight-deck": {
    variant: "deck",
    caption: "COUNTRY QBR",
  },
  "monthly-scorecard": {
    variant: "table",
    caption: "SCORECARD",
  },
  "1p-qsr-app-audit": {
    variant: "deck",
    caption: "1P APP AUDIT",
  },
  "1p-epl-app-audit": {
    variant: "deck",
    caption: "1P APP AUDIT",
  },
};
